import { createContext, useContext, useState } from 'react';

interface MockUser {
    email: string;
    attributes: {
        email: string;
        sub: string;
    };
}

interface MockAuthContextType {
    isAuthenticated: boolean;
    user: MockUser | null;
    isLoading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    confirmSignUp: (email: string, code: string) => Promise<void>;
}

const MockAuthContext = createContext<MockAuthContextType | null>(null);

// Simulated delay to mimic API calls
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const MockAuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        const stored = localStorage.getItem('mockAuth');
        return !!stored;
    });
    const [user, setUser] = useState<MockUser | null>(() => {
        const stored = localStorage.getItem('mockAuth');
        if (stored) {
            const { email } = JSON.parse(stored);
            return {
                email,
                attributes: {
                    email,
                    sub: 'mock-user-id'
                }
            };
        }
        return null;
    });

    // Simulate local storage persistence
    const saveToStorage = (email: string) => {
        localStorage.setItem('mockAuth', JSON.stringify({ email }));
    };

    const clearStorage = () => {
        localStorage.removeItem('mockAuth');
    };

    const signIn = async (email: string, password: string) => {
        await delay(500); // Simulate API delay

        // Mock validation
        if (password === 'wrong') {
            throw new Error('Invalid credentials');
        }

        const mockUser = {
            email,
            attributes: {
                email,
                sub: 'mock-user-id'
            }
        };

        setIsAuthenticated(true);
        setUser(mockUser);
        saveToStorage(email);
    };

    const signUp = async (email: string, password: string) => {
        await delay(500);

        // Mock validation
        if (email === 'exists@example.com') {
            throw new Error('User already exists');
        }
        console.log('signUp', email, password);

        // In mock mode, we'll auto-confirm the user
        return Promise.resolve();
    };

    const confirmSignUp = async (email: string, code: string) => {
        await delay(500);
        console.log('confirmSignUp', email, code);
        // Mock validation
        if (code !== '123456') {
            throw new Error('Invalid verification code');
        }

        return Promise.resolve();
    };

    const signOut = async () => {
        await delay(500);
        setIsAuthenticated(false);
        setUser(null);
        clearStorage();
    };

    return (
        <MockAuthContext.Provider
            value={{
                isAuthenticated,
                user,
                isLoading: false,
                signIn,
                signUp,
                signOut,
                confirmSignUp,
            }}
        >
            {children}
        </MockAuthContext.Provider>
    );
};

export const useMockAuth = () => {
    const context = useContext(MockAuthContext);
    if (!context) {
        throw new Error('useMockAuth must be used within a MockAuthProvider');
    }
    return context;
};